import {
  useState,
  useCallback,
  useMemo,
  KeyboardEvent,
} from 'react';

import { EntityIcon } from 'web-components';
import { useClickAway } from 'web-hooks';
import { Flex, SVG } from 'web-styled';

import type { SearchSectionItem } from './types';
import * as S from './styled';

const searchLinkMap = {
  clients: 'users',
  exercises: 'exercises',
  workouts: 'workout_templates',
  programs: 'program_templates',
  documents: 'documents',
} as const;

type Props = {
  searchQuery: string;
  searchSections: SearchSectionItem[];
  setSearchQuery: (text: string) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  onSearchSubmit: () => void;
};

const SearchSection = ({
  searchQuery,
  searchSections,
  setSearchQuery,
  onKeyDown,
  onSearchSubmit,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasQuery = Boolean(searchQuery.trim());
  const hasResults = searchSections.length > 0;
  const showDropdown = isOpen && hasQuery;
  const clickAwayRef = useClickAway(() => setIsOpen(false), { disabled: !showDropdown });
  const resultCount = useMemo(
    () => searchSections.reduce((acc, section) => acc + section.items.length, 0),
    [searchSections],
  );

  const handleChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setIsOpen(true);
  }, [setSearchQuery]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    onKeyDown(event);
    if (event.key === 'Enter' && hasQuery) {
      setIsOpen(false);
    }
  }, [hasQuery, onKeyDown]);

  const handleClear = useCallback(() => {
    if (!searchQuery) return;
    setSearchQuery('');
    setIsOpen(false);
  }, [searchQuery, setSearchQuery]);

  const handleFocus = useCallback(() => {
    if (hasQuery) {
      setIsOpen(true);
    }
  }, [hasQuery]);

  const handleResultClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleViewAllClick = useCallback(() => {
    onSearchSubmit();
    setIsOpen(false);
  }, [onSearchSubmit]);

  return (
    <S.Search data-tour="tour__search" ref={clickAwayRef}>
      <S.InputGroup>
        <S.Input
          placeholder="Search your library"
          value={searchQuery}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
        />
        <S.SearchButton
          onClick={searchQuery ? handleClear : undefined}
          type="button"
        >
          {searchQuery ? <SVG.X size={13} /> : <SVG.Search size={13} />}
        </S.SearchButton>
      </S.InputGroup>
      {showDropdown && (
        <S.SearchDropdown>
          {hasResults ? (
            <>
              {searchSections.map((section) => (
                <S.SearchCategory key={section.key}>
                  <S.SearchCategoryTitle>{section.label}</S.SearchCategoryTitle>
                  {section.items.map((item) => (
                    <S.SearchResultLink
                      key={`${section.key}-${item.id}`}
                      to={item.entityType === 'clients'
                        ? `/${searchLinkMap[item.entityType]}/${item.id}/programs`
                        : `/${searchLinkMap[item.entityType]}/${item.id}`}
                      onClick={handleResultClick}
                    >
                      <Flex spacing={10} align="center">
                        <EntityIcon resourceType={item.entityType} />
                        <S.SearchResultName>{item.name}</S.SearchResultName>
                      </Flex>
                    </S.SearchResultLink>
                  ))}
                </S.SearchCategory>
              ))}
              <S.ViewAllButton type="button" onClick={handleViewAllClick}>
                View all {resultCount} result{resultCount === 1 ? '' : 's'}
              </S.ViewAllButton>
            </>
          ) : (
            <S.SearchEmpty>No results found</S.SearchEmpty>
          )}
        </S.SearchDropdown>
      )}
    </S.Search>
  );
};

export default SearchSection;
